import type { WorkbookDashboard, WorkbookSheet } from "../../../shared/account-finance/finance-workbook-contract";

export type WorkbookLane = "cash" | "incoming" | "outgoing" | "net";
export const workbookLaneSheets: Record<WorkbookLane, readonly string[]> = {
  cash: ["100", "102"], incoming: ["101", "121"], outgoing: ["103", "321", "kredi-taksitleri"], net: [],
};
export function workbookRowIncluded(sheet: Pick<WorkbookSheet, "kind">,
  row: Pick<WorkbookSheet["rows"][number], "direction" | "status">) {
  if (sheet.kind === "ledger" || sheet.kind === "schedule") return false;
  if (sheet.kind !== "instrument") return true;
  return (row.direction === "incoming" ? ["0", "2", "3", "9"] : ["1", "9"]).includes(row.status);
}
/** Resolve duplicate source identities before applying dates, search or currency filters. */
export function workbookUniqueRows(sheet: Pick<WorkbookSheet, "rows">): WorkbookSheet["rows"] {
  const seen = new Set<string>();
  return sheet.rows.filter(row => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
}
export function workbookSheetTotal(sheet: WorkbookSheet, currency: string, seen = new Set<string>()) {
  return workbookUniqueRows(sheet).reduce((total, row) => {
    if (row.currency !== currency || seen.has(row.id)) return total;
    seen.add(row.id);
    if (!workbookRowIncluded(sheet, row)) return total;
    return total + BigInt(row.remaining);
  }, 0n);
}
export function workbookLaneTotal(book: WorkbookDashboard, lane: WorkbookLane, currency: string): bigint | null {
  if (lane === "net") return null;
  const ids = workbookLaneSheets[lane];
  const sheets = ids.map(id => book.sheets.find(sheet => sheet.id === id));
  if (sheets.some(sheet => !sheet || sheet.hasMore || (sheet.date && book.date && sheet.date !== book.date)
    || (sheet.company && book.company && sheet.company !== book.company)) || book.errors.some(error => ids.includes(error.id))) return null;
  // Identities belong to a source; two different source tables can use the same row id.
  return sheets.reduce((sum, sheet) => sum + (sheet ? workbookSheetTotal(sheet, currency) : 0n), 0n);
}
export function workbookVisibleRows(sheet: WorkbookSheet, search: string) {
  const term = search.trim().toLocaleLowerCase("tr-TR");
  return workbookUniqueRows(sheet).filter(row => !term || `${row.code} ${row.name} ${row.document} ${row.status} ${row.date ?? ""}`.toLocaleLowerCase("tr-TR").includes(term));
}

export function workbookCurrencies(sheets: readonly WorkbookSheet[]) {
  const priority = ["TL", "USD", "EUR", "GBP"];
  return [...new Set(sheets.flatMap(sheet => sheet.rows.map(row => row.currency)))].sort((a, b) => {
    const left = priority.indexOf(a), right = priority.indexOf(b);
    return (left < 0 ? priority.length : left) - (right < 0 ? priority.length : right) || a.localeCompare(b);
  });
}

export function workbookLaneAmounts(book: WorkbookDashboard, lane: WorkbookLane) {
  const sheets = book.sheets.filter(sheet => workbookLaneSheets[lane].includes(sheet.id));
  return workbookCurrencies(sheets).map(currency => ({ currency, total: workbookLaneTotal(book, lane, currency) }));
}

export const workbookGroups = [
  { id: "cash", name: "Kasa ve bankalar", preferred: "102" },
  { id: "cheques", name: "Çek ve senetler", preferred: "101" },
  { id: "loans", name: "Krediler", preferred: "kredi-taksitleri" },
  { id: "suppliers", name: "Tedarikçiler", preferred: "tedarikci-odemeleri" },
  { id: "customers", name: "Müşteriler", preferred: "tahsilatlar" },
  { id: "expenses", name: "Gider hesapları", preferred: "770" },
  { id: "other", name: "Diğer hesaplar", preferred: "" },
  { id: "plans", name: "Ödeme planları", preferred: "manuel-plan" },
] as const;

export function workbookGroupForSheet(id: string) {
  if (id === "manuel-plan") return "plans";
  if (id === "tahsilatlar") return "customers";
  if (id === "tedarikci-odemeleri") return "suppliers";
  if (id === "leasing") return "loans";
  if (["720", "730", "740", "760", "770", "780"].includes(id)) return "expenses";
  if (["100", "102", "108"].includes(id)) return "cash";
  if (["101", "103", "121", "321", "421"].includes(id)) return "cheques";
  if (id === "kredi-taksitleri" || /^(30|40)\d$/u.test(id)) return "loans";
  if (["320", "329", "420", "429"].includes(id)) return "suppliers";
  if (["120", "127", "128", "129"].includes(id)) return "customers";
  return "other";
}

export function workbookSheetLabel(id: string, name: string) {
  const names: Record<string, string> = {
    "100": "Kasalar", "102": "Banka hesapları", "101": "Alınan çekler", "103": "Verilen çekler",
    "121": "Alacak senetleri", "321": "Borç senetleri", "120": "Müşteri hesapları",
    "320": "Tedarikçi hesapları", "300": "Kısa vadeli kredi hesapları", "400": "Uzun vadeli kredi hesapları",
    "kredi-taksitleri": "Kredi taksitleri",
  };
  const lower = name.toLocaleLowerCase("tr-TR");
  return names[id] ?? (lower.charAt(0).toLocaleUpperCase("tr-TR") + lower.slice(1));
}
